import type { VesselDetailsValidationContext } from "../models/tech-form-validation-ui.models";
import type {
  DraftValidationContext,
  FieldErrors,
  NumericInputMode,
  DraftTableErrors,
  PowerManagementTableErrors,
} from "../models/tech-form-validation-ui.models";
import { TECH_FORM_VALIDATION_CONFIG } from "../models/tech-form-validation.constants";
import type { TechFormSectionNumber, TechFormValidationValues } from "../models/tech-form-validation.models";
import type { Tq1VesselDetailsM } from "../models/tq1-vessel-details.models";
import type { Tq2DesignDetailsM } from "../models/tq2-design-details.models";
import type { TableRow, Tq3DraftM } from "../models/tq3-draft.models";
import type { Tq4AntifoulingM } from "../models/tq4-antifouling.models";
import type { Tq6SecaM } from "../models/tq6-seca.models";
import type { Tq7SlowSteamingM } from "../models/tq7-slow-steaming.models";
import type { PowerManagementItem, Tq8PowerManagementM } from "../models/tq8-power-management.models";
import { createTechFormValidator } from "./create-tech-form-validator.utils";

export type {
  DraftTableErrors,
  PowerManagementTableErrors,
  NumericInputMode,
} from "../models/tech-form-validation-ui.models";
export { FAKE_VESSEL_IMO_NUMBER_WARNING } from "../models/tech-form-validation-ui.constants";

export const techFormValidator = createTechFormValidator(TECH_FORM_VALIDATION_CONFIG);

// Preserve the entered text; validation accepts comma decimals without changing invalid expressions.
export const sanitizeTechFormNumericInput = (value: unknown, _mode: NumericInputMode): string => String(value ?? "");
export const sanitizeTechFormInputForField = (_fieldName: unknown, value: unknown): string => String(value ?? "");
export const isFakeVesselImoNumberForTesting = (value: unknown): boolean =>
  String(value ?? "").trim() === TECH_FORM_VALIDATION_CONFIG.fakeImo;

function sectionErrors<T extends object>(
  section: TechFormSectionNumber,
  form: T,
  extra: Partial<TechFormValidationValues> = {},
): FieldErrors<T> {
  return Object.fromEntries(
    techFormValidator
      .validateSection(section, {
        ...extra,
        sections: { ...extra.sections, [section]: form },
      })
      .filter((issue) => !issue.key.startsWith("tq3DraftTable") && !issue.key.startsWith("tq8PowerManagementTable"))
      .map((issue) => [issue.key, issue.message]),
  ) as FieldErrors<T>;
}
export const validateTq1VesselDetails = (form: Tq1VesselDetailsM, context: VesselDetailsValidationContext = {}) =>
  sectionErrors(1, form, { sections: { 3: { summerDWT: context.summerDWT } } });
export const validateTq2DesignDetails = (form: Tq2DesignDetailsM) => sectionErrors(2, form);
export const validateTq3Draft = (form: Tq3DraftM, context: DraftValidationContext = {}) =>
  sectionErrors(3, form, { sections: { 1: { lightWeight: context.lightWeight } } });
export const validateTq4Antifouling = (form: Tq4AntifoulingM) => sectionErrors(4, form);
export const validateTq6Seca = (form: Tq6SecaM) => sectionErrors(6, form);
export const validateTq7SlowSteaming = (form: Tq7SlowSteamingM) => sectionErrors(7, form);
export const validateTq8PowerManagement = (form: Tq8PowerManagementM, schemaVersion?: number | null) =>
  sectionErrors(8, form, { schemaVersion });

export function validateTq3DraftTable(rows: TableRow[], context: DraftValidationContext = {}): DraftTableErrors {
  const errors: DraftTableErrors = {};
  const issues = techFormValidator.validateSection(3, {
    sections: { 3: { summerSWDraft: context.summerSWDraft } },
    draftRows: rows.map((row) => ({ ...row })),
  });
  issues
    .filter((issue) => issue.key.startsWith("tq3DraftTable."))
    .forEach((issue) => {
      const [, index, key] = issue.key.split(".");
      const row = rows[Number(index)];
      if (row) errors[row.id] = { ...errors[row.id], [key]: issue.message };
    });
  if (rows.length !== TECH_FORM_VALIDATION_CONFIG.draftCount)
    errors[-1] = { draft: "Provide exactly five complete rows" };
  return errors;
}
export function validatePowerManagementTable(
  rows: PowerManagementItem[],
  form: Tq8PowerManagementM,
  scrubberFitted?: unknown,
  schemaVersion?: number | null,
): PowerManagementTableErrors {
  const errors: PowerManagementTableErrors = {};
  techFormValidator
    .validateSection(8, {
      sections: { 8: { ...form }, 2: { scrubberFitted } },
      schemaVersion,
      powerRows: rows.map((row) => ({ ...row })),
    })
    .filter((issue) => issue.key.startsWith("tq8PowerManagementTable."))
    .forEach((issue) => {
      const [, index, key] = issue.key.split(".");
      const id = rows[Number(index)]?.id ?? Number(index) + 1;
      errors[id] = { ...errors[id], [key]: issue.message };
    });
  return errors;
}
